/**
 * Payment Verification Success Page
 * Beautiful branded display for payment verification results
 */

import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Divider,
  Chip,
  Stack,
  Alert,
  Container,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Receipt as ReceiptIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { paystackPaymentService } from '../../services/paystackPaymentService';

interface PaymentData {
  status: string;
  amount: number;
  currency: string;
  paidAt: string;
  channel: string;
  reference: string;
  customer?: {
    email: string;
  };
  metadata?: {
    bookingId?: string;
    userId?: string;
  };
}

const PaymentVerificationPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [paymentData, setPaymentData] = useState<PaymentData | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    verifyPayment();
  }, []);

  const verifyPayment = async () => {
    try {
      const reference = searchParams.get('reference') || searchParams.get('trxref');

      if (!reference) {
        setStatus('error');
        setErrorMessage('No payment reference found in URL');
        return;
      }

      console.log('🔍 Verifying payment with reference:', reference);
      const result = await paystackPaymentService.verifyPayment(reference);
      console.log('📦 API Response:', result);

      if (result.success && result.data) {
        setPaymentData(result.data);
        console.log('✅ Payment data:', result.data);
        console.log('📊 Payment status:', result.data.status);

        // Check if payment was successful
        if (result.data.status === 'success') {
          setStatus('success');
          console.log('🎉 Payment verified successfully!');
        } else {
          setStatus('error');
          setErrorMessage(`Payment status: ${result.data.status || 'unknown'}`);
          console.warn('⚠️ Payment verification returned non-success status:', result.data.status);
        }
      } else {
        setStatus('error');
        setErrorMessage(result.error || 'Failed to verify payment');
        console.error('❌ API returned error:', result.error);
      }
    } catch (error) {
      console.error('💥 Payment verification exception:', error);
      setStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'An unexpected error occurred');
    }
  };

  const formatAmount = (amount: number, currency: string) => {
    // Amount is in kobo/cents, convert to main unit
    const mainAmount = amount / 100;
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: currency,
    }).format(mainAmount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleRetry = () => {
    setStatus('loading');
    verifyPayment();
  };

  const handleClose = () => {
    window.close();
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Card
          elevation={3}
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <CardContent sx={{ p: 4 }}>
            {/* Loading State */}
            {status === 'loading' && (
              <Stack spacing={3} alignItems="center">
                <CircularProgress size={64} thickness={4} />
                <Typography variant="h5" fontWeight={600}>
                  Verifying Payment
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  Please wait while we verify your payment with our payment provider...
                </Typography>
              </Stack>
            )}

            {/* Success State */}
            {status === 'success' && paymentData && (
              <Stack spacing={3}>
                {/* Success Icon */}
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      bgcolor: 'success.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      animation: 'scaleIn 0.3s ease-out',
                      '@keyframes scaleIn': {
                        '0%': { transform: 'scale(0)' },
                        '100%': { transform: 'scale(1)' },
                      },
                    }}
                  >
                    <CheckCircleIcon sx={{ fontSize: 48, color: 'white' }} />
                  </Box>
                </Box>

                {/* Title */}
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight={700} gutterBottom>
                    Payment Verified!
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Your payment has been successfully verified and processed
                  </Typography>
                </Box>

                {/* Payment Details */}
                <Box>
                  <Divider sx={{ mb: 2 }}>
                    <Chip label="Payment Details" size="small" />
                  </Divider>

                  <Stack spacing={2}>
                    {/* Amount */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        Amount Paid
                      </Typography>
                      <Typography variant="h6" fontWeight={600} color="success.main">
                        {formatAmount(paymentData.amount, paymentData.currency)}
                      </Typography>
                    </Box>

                    {/* Reference */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        Reference
                      </Typography>
                      <Typography variant="body2" fontWeight={500} fontFamily="monospace">
                        {paymentData.reference}
                      </Typography>
                    </Box>

                    {/* Date */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        Date & Time
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {formatDate(paymentData.paidAt)}
                      </Typography>
                    </Box>

                    {/* Payment Method */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        Payment Method
                      </Typography>
                      <Chip
                        label={paymentData.channel.toUpperCase()}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </Box>

                    {/* Status */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        Status
                      </Typography>
                      <Chip
                        label="VERIFIED"
                        size="small"
                        color="success"
                        icon={<CheckCircleIcon />}
                      />
                    </Box>

                    {/* Customer Email */}
                    {paymentData.customer?.email && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          Email
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {paymentData.customer.email}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Box>

                {/* Info Alert */}
                <Alert severity="info" icon={<ReceiptIcon />}>
                  A receipt has been sent to your email address
                </Alert>

                {/* Success Message */}
                <Alert severity="success">
                  <Typography variant="body2">
                    <strong>Payment completed successfully!</strong><br />
                    You can now safely close this window. We've sent a confirmation email to {paymentData.customer?.email}.
                  </Typography>
                </Alert>
              </Stack>
            )}

            {/* Error State */}
            {status === 'error' && (
              <Stack spacing={3}>
                {/* Error Icon */}
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      bgcolor: 'error.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <ErrorIcon sx={{ fontSize: 48, color: 'white' }} />
                  </Box>
                </Box>

                {/* Title */}
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight={700} gutterBottom>
                    Verification Failed
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {errorMessage || 'We could not verify your payment'}
                  </Typography>
                </Box>

                {/* Error Alert */}
                <Alert severity="error">
                  <Typography variant="body2">
                    <strong>What should I do?</strong><br />
                    • Try verifying your payment again by clicking "Try Again" below<br />
                    • If the problem persists, contact our support team with your payment reference<br />
                    • You can safely close this window if you've already contacted support
                  </Typography>
                </Alert>

                {/* Payment Reference if available */}
                {(paymentData?.reference || searchParams.get('reference') || searchParams.get('trxref')) && (
                  <Box sx={{ textAlign: 'center', bgcolor: 'background.paper', p: 2, borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                      Payment Reference (save this for support):
                    </Typography>
                    <Typography variant="body2" fontFamily="monospace" fontWeight={600}>
                      {paymentData?.reference || searchParams.get('reference') || searchParams.get('trxref')}
                    </Typography>
                  </Box>
                )}

                {/* Action Buttons */}
                <Stack spacing={2}>
                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    startIcon={<RefreshIcon />}
                    onClick={handleRetry}
                  >
                    Try Again
                  </Button>
                  <Typography variant="caption" color="text.secondary" textAlign="center">
                    You can close this window after contacting support
                  </Typography>
                </Stack>
              </Stack>
            )}
          </CardContent>
        </Card>

        {/* PolarRide Branding */}
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography variant="caption" color="text.secondary">
            Powered by PolarRide
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default PaymentVerificationPage;
